<template>
  <el-card shadow="never" class="card-pond-select-with-details" v-loading="loading">
    <el-row class="pond-details-row" align="middle" justify="space-between" :key="pondId">
      <el-col :span="3" class="item-container">
        <div class="item-container__body">
          <el-row class="item-header">{{ $t("Comn_pond_name") }}</el-row>
          <el-row class="item-body">
            <er-select
              :value="pondSelected"
              value-key="_id"
              @change="handleChangeInPond"
              filterable
              styleType="no-border"
            >
              <el-option
                v-for="(pond, pondId) in sortedPondObj"
                :key="pondId"
                :label="pond.title"
                :value="pond"
              ></el-option>
            </er-select>
          </el-row>
        </div>
      </el-col>
      <el-col :span="8" class="item-container">
        <div class="item-container__body">
          <el-row class="item-header normal-text">{{ $t("Pond_pls_stocked") }}</el-row>
          <el-row class="item-body">
            <b>{{ getCurrentCulturePLStocked | commas(getUserCountryCode) | na("-") }}</b>
          </el-row>
        </div>
        <div class="item-container__body">
          <el-row class="item-header normal-text">{{ $t("Ponds_water_spread_area") }}</el-row>
          <el-row
            class="item-body"
            :data-suffix="
              getUserCountryName.toLowerCase() === 'india' ? 'ac' : 'ha'
            "
          >
            {{
            updatePondSizeBasedOnUnits(pondSelected)
            | numberWith2DigitPrecision
            | na("-")
            }}
          </el-row>
        </div>
        <div class="item-container__body" :span="9">
          <el-row class="item-header normal-text">{{ $t("Comn_doc") }}</el-row>
          <el-row class="item-body" :data-suffix="$t('Comn_days_new')">
            {{
            daysOfCulture | na("-")
            }}
          </el-row>
        </div>
      </el-col>
      <el-col :span="10" class="item-container">
        <div class="item-container__body">
          <el-row class="item-header normal-text">
            {{
            $t("Comn_survival_rate")
            }}
          </el-row>
          <el-row
            class="item-body"
            :data-suffix="getSuffix(netAbwSummaryvalues.survival_percentage,'survival_percentage')"
          >
            {{
            netAbwSummaryvalues.survival_percentage|na("-")
            }}
          </el-row>
        </div>
        <div :span="9" class="item-container__body">
          <el-row class="item-header normal-text">
            {{
            $t("Comn_biomass")
            }}
          </el-row>
          <el-row
            class="item-body"
            :data-suffix="
            getSuffix(netAbwSummaryvalues.biomass,'biomass')
            "
          >
            {{
            netAbwSummaryvalues.biomass| numberWithNDigitPrecision(0)
            | formatThousandStr| na("-")
            }}
          </el-row>
        </div>
        <div class="item-container__body">
          <el-row class="item-header normal-text">
            {{
            $t("Comn_net_feed")
            }}
          </el-row>
          <el-row
            class="item-body"
            :data-suffix="
              getSuffix(netFeedSummaryValues.total_dispensed_feed,'net_feed')
            "
          >
            {{
            netFeedSummaryValues.total_dispensed_feed
            | numberWithNDigitPrecision(1)
            | formatThousandStr
            | na("-")
            }}
          </el-row>
        </div>
        <div :span="9" class="item-container__body">
          <el-row class="item-header normal-text">{{ $t("ABW_abw") }}</el-row>
          <el-row
            class="item-body"
            :data-suffix="getSuffix(netAbwSummaryvalues.abw,'abw')"
          >{{ netAbwSummaryvalues.abw | numberWithNDigitPrecision(1) | na("-") }}</el-row>
        </div>
        <div :span="9" class="item-container__body">
          <el-row class="item-header normal-text">{{ $t("Comn_awg") }}</el-row>
          <el-row
            class="item-body"
            :data-suffix="
              getSuffix(netAbwSummaryvalues.awg,'awg')
            "
          >{{ netAbwSummaryvalues.awg | numberWithNDigitPrecision(1) | na("-") }}</el-row>
        </div>
      </el-col>
      <el-col :span="3" class="item-container" :key="$i18n.locale">
        <div class="item-container__body">
          <el-row class="item-header">{{ $t("Comn_current_culture") }}</el-row>
          <el-row class="item-body">
            <er-select
              v-if="getCultures.length > 0"
              :value="getCurrentCulture"
              value-key="harvest_id"
              @input="handleChangeInCulture"
              styleType="no-border"
            >
              <el-option
                v-for="(cultivation, index) in getCultures"
                :key="index"
                :label="cultivation.start_date_string"
                :value="cultivation"
              >{{cultivation.name}}</el-option>
            </er-select>

            <el-tag
              type="info"
              :disable-transitions="true"
              v-else
            >{{ $t("PM_culture_not_started_msg") }}</el-tag>
          </el-row>
        </div>
      </el-col>
    </el-row>
  </el-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";

import filtersMixin from "@/mixins/filtersMixin.js";
import moment from "moment";
// import { MOMENT_DATE_FORMAT_WITH_YEAR } from "@/utils/commonUtils";
export default {
  mixins: [errorHandlerMixin, filtersMixin],
  data: function() {
    return {
      loading: false
    };
  },
  filters: {
    numberWith2DigitPrecision(value) {
      if (!value) return 0;
      value = Number(value);
      if (Number.isInteger(value)) {
        return value;
      } else {
        return value.toFixed(2);
      }
    }
  },
  computed: {
    ...mapGetters("pondDetails", {
      pondsObj: "getPondIdPondMap",
      pondId: "getSelectedPondId",
      getSelectedPond: "getSelectedPondWithDeviceDetails",
      getUserCountryName: "getUserCountryName",
      getUserTimeZoneOffset: "getUserTimeZoneOffset",
      getCultures: "getAllCultureOfPond",
      getCurrentCulture: "getCurrentCulture",
      getCurrentCulturePLStocked: "getCurrentCulturePLStocked",
      getSelectedNetFeedSummary: "getSelectedNetFeedSummary",
      getSelectedPondAbwSummary: "getSelectedPondAbwSummary"
    }),
    ...mapGetters("user", {
      getUserCountryCode: "getUserCountryCode",
      getUserCountryName: "getUserCountryName"
    }),
    updatePondSizeBasedOnUnits() {
      return pondSelected => {
        const size = (pondSelected || { size: 0 }).size;
        const units = (pondSelected || { units: "ha" }).units;
        const userLocation = this.getUserCountryName.toLowerCase();
        if (userLocation === "india") {
          return this.$commonUtils.castPondAreaSize(size, units, "ac");
        }
        return this.$commonUtils.castPondAreaSize(size, units, "ha");
      };
    },
    getSuffix() {
      const unitsType = {
        abw: "g",
        awg: "g",
        net_feed: "Kgs",
        biomass: "Kgs",
        survival_percentage: "%"
      };
      return (quantity, quantityType) => {
        if (isNaN(+quantity)) {
          return "";
        }
        return unitsType[quantityType];
      };
    },
    daysOfCulture: function() {
      const value = this.getCurrentCulture.doc;
      return value > 0
        ? value + 1
        : this.getCurrentCulture.start_date && value === 0
        ? "Today"
        : "--";
    },
    netAbwSummaryvalues: function() {
      return this.getSelectedPondAbwSummary;
    },
    netFeedSummaryValues: function() {
      return this.getSelectedNetFeedSummary;
    },
    pondSelected() {
      return this.$lodash.cloneDeep(this.getSelectedPond);
    },
    sortedPondObj() {
      const objSortPondList = {};
      const list = Object.values(this.pondsObj).sort((a, b) => {
        return this.$commonUtils.naturalSort("title")(a, b);
      });
      list.forEach(pond => {
        objSortPondList[pond._id] = pond;
      });
      return objSortPondList;
    },
    startCultureDate() {
      var date = moment(this.getCurrentCulture.start_date).format(
        "DD MMM, YYYY"
      );
      return date;
    }
  },
  async mounted() {
    this.$moment.locale(this.$i18n.locale);
    // this.moonPhases = this.getFourMoonPhases(this.$moment());
  },
  methods: {
    ...mapActions("pondDetails", {
      changeSelectedPondId: "changeSelectedPondId",
      changeCulture: "changeCulture"
    }),
    async handleChangeInPond(pond) {
      this.loading = true;
      try {
        await this.changeSelectedPondId(pond._id);
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    },
    dateFormat(isoDateString) {
      return this.$moment.utc(isoDateString).format("YYYY-MM-DD");
    },
    async handleChangeInCulture(cultureObj) {
      await this.changeCulture(cultureObj);
    }
  }
};
</script>

<style lang="scss">
.card-pond-select-with-details {
  margin-bottom: 10px;
  .el-card__body {
    // @include responsiveProperty(height, 65px, 75px, 90px);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0px 10px;
    @include responsiveProperty(
      font-size,
      $app_font_size_1,
      $app_font_size_2,
      $app_font_size_3
    );

    .pond-details-row {
      width: 100%;
      .item-container {
        display: flex;
        flex-direction: row;
        color: #061058;
        justify-content: space-between;
        &:not(:last-child) {
          border-right: 1px solid #ececec;
        }
        &:not(:first-child) {
          padding-left: 15px;
        }
        &:not(:last-child) {
          padding-right: 10px;
        }
        .item-container__body {
          padding: 10px 0px;
          .item-header {
            opacity: 0.5;
            // @include responsiveProperty(max-width, 75px, 160px, 250px);
            // white-space: nowrap;
            // overflow: hidden;
            // text-overflow: ellipsis;
          }
          .item-body {
            font-weight: 600;
            height: 30px;
            line-height: 30px;
            .el-input__inner {
              font-weight: 600;
              line-height: 30px;
              padding-left: 0px;
              padding-right: 23px;
              height: 30px;
            }
            .el-input__icon {
              line-height: 30px;
            }
            &:after {
              content: attr(data-suffix);
              display: inline;
              margin-left: 5px;
              @include responsiveProperty(
                font-size,
                $app_font_size_small,
                $app_font_size_1,
                $app_font_size_2
              );
            }
          }
        }
      }
    }
  }
  .el-input__inner {
    @include responsiveProperty(
      font-size,
      $app_font_size_1,
      $app_font_size_2,
      $app_font_size_3
    );
  }
  .select-pond,
  .culture-select {
    .el-select {
      width: 100%;
      .el-input__inner {
        font-weight: bold;
        color: #3f98cb;
      }
    }
  }
  .el-row--flex {
    display: flex;
    align-items: center;
  }

  .divisional-line {
    display: inline-block;
    border: 1px solid #ccc;
    margin: 0 20px;
    height: 63px;
  }
  .el-popper .popper__arrow::after {
    content: " ";
    border-width: 0px;
  }
  .el-popper[x-placement^="bottom"] .popper__arrow::after {
    border-bottom: none;
  }
}
</style>
